import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { getCampaignById } from '../services/campaignService';
import { Campaign } from '../types/campaign';
import '../styles/ClaimPrizePage.css';
import Footer from '../components/Footer';

const ClaimPrizePage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchCampaign = async () => {
      if (!campaignId) {
        setLoading(false);
        return;
      }
      
      const campaignData = await getCampaignById(campaignId);
      setCampaign(campaignData);
      setLoading(false);
    };

    fetchCampaign();
  }, [campaignId, currentUser, navigate]);

  const handleClaimPrize = async () => {
    if (!currentUser || !campaignId || !campaign) return;

    setClaiming(true);
    try {
      // NOTE: This manual prize claiming is deprecated.
      // Prizes should be created automatically via webhook from Playable.
      // Prize data (prizeName, numberOfPrizes) comes from webhook, not campaign document.
      
      alert('This feature is deprecated. Prizes are now created automatically when you win via the game. Please play the game to win prizes.');
      navigate('/main');
      
      // OLD CODE (kept for reference but disabled):
      // const prizeData = {
      //   userId: currentUser.uid,
      //   campaignId,
      //   brandName: campaign.brandName,
      //   brandLogo: campaign.brandLogo,
      //   prizeTitle: '???', // No longer available in campaign
      //   prizeValue: '???', // No longer available in campaign
      //   category: campaign.category,
      //   claimedAt: serverTimestamp(),
      //   expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      //   isRedeemed: false
      // };
      // await addDoc(collection(db, 'prizes'), prizeData);
    } catch (error) {
      console.error('Error claiming prize:', error);
      alert('Der opstod en fejl. Prøv igen.');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="claim-prize-page">
        <div className="claim-loading">
          <div className="spinner"></div>
          <p>Indlæser...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="claim-prize-page">
        <div className="claim-error">
          <h2>Kampagne ikke fundet</h2>
          <button onClick={() => navigate('/main')} className="claim-back-button">
            Tilbage til hovedside
          </button>
        </div>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="claim-prize-page">
        <div className="claim-success">
          <div className="claim-success-icon">✓</div>
          <h2 className="claim-success-title">Din præmie er nu hentet!</h2>
          <p className="claim-success-message">Præmien er gemt i din wallet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-prize-page fade-in">
      {/* Background */}
      <div className="claim-background"></div>

      {/* Content */}
      <div className="claim-content">
        <div className="claim-header">
          <h1 className="claim-title">🎉 Tillykke!</h1>
          <h2 className="claim-subtitle">Du har vundet!</h2>
        </div>

        <div className="claim-prize-card">
          <img 
            src={campaign.brandLogo} 
            alt={campaign.brandName} 
            className="claim-brand-logo"
          />
          <h3 className="claim-prize-title">{campaign.title}</h3>
          <p className="claim-prize-value">Prize details from Playable</p>
          <div className="claim-prize-category">{campaign.category}</div>
        </div>

        <button 
          className="claim-button" 
          onClick={handleClaimPrize}
          disabled={claiming}
        >
          {claiming ? 'Gemmer...' : 'Gem præmien i min Wallet'}
        </button>

        <button 
          className="claim-back-link" 
          onClick={() => navigate('/main')}
        >
          Tilbage til kampagner
        </button>
      </div>

      {/* Footer Navigation */}
      <Footer />
    </div>
  );
};

export default ClaimPrizePage;

